/**
 * @file IndividualDataPointsTab.js
 * @description Displays data points for an individual with sorting, column filtering, and pagination.
 */

import { useState, useEffect, useMemo } from 'react';
import classNames from 'classnames';
import {
    getDataPointCategories,
    getDataPointSubCategories,
    getDataPointsByIndividualId,
} from '../../services/individualService';
import Spinner from '../ui/spinners/Spinner';
import NotFoundBox from '../ui/no_data_indicators/NotFoundBox';
import PaginationComponent from '../helpers/PaginationComponent';
import { FilterModal, renderFilterIcon } from '../helpers/FilterComponent';
import styles from '../data-points/DataPoints.module.scss';
import { getText } from '../../i18n';

const ITEMS_PER_PAGE = 20;

const SortOptions = {
    CATEGORY: 'categoryName',
    SUBCATEGORY: 'subcategoryName',
    DATE: 'date',
    AMOUNT: 'amount',
};

const OrderOptions = {
    ASCENDING: 'asc',
    DESCENDING: 'desc',
};

const formatDate = isoString => {
    if (!isoString) return '—';
    const [year, month, day] = isoString.split('-');
    return `${month}/${day}/${year}`;
};

const formatAmount = amount => {
    if (amount == null) return '—';
    return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(amount);
};

function IndividualDataPointsTab({ individualId }) {
    const [allDataPoints, setAllDataPoints] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selections, setSelections] = useState({});
    const [activeModal, setActiveModal] = useState(null);
    const [sortConfig, setSortConfig] = useState({
        sort: SortOptions.DATE,
        order: OrderOptions.DESCENDING,
    });
    const [currentPage, setCurrentPage] = useState(1);

    useEffect(() => {
        async function loadData() {
            setIsLoading(true);
            try {
                const [points, categories, subcategories] = await Promise.all([
                    getDataPointsByIndividualId(individualId),
                    getDataPointCategories(),
                    getDataPointSubCategories(),
                ]);

                const categoryMap = Object.fromEntries(
                    categories.map(c => [
                        c.id,
                        { code: c.code, enabled: c.enabled ?? true },
                    ]),
                );
                const subcategoryMap = Object.fromEntries(
                    subcategories.map(s => [
                        s.id,
                        { code: s.code, enabled: s.enabled ?? true },
                    ]),
                );

                const enriched = points
                    .filter(p => {
                        const category = categoryMap[p.dataPointCategoryId];
                        const subcategory =
                            subcategoryMap[p.dataPointSubcategoryId];
                        const subcategoryAllowed =
                            !p.dataPointSubcategoryId || subcategory?.enabled;
                        return category?.enabled && subcategoryAllowed;
                    })
                    .map(p => ({
                        ...p,
                        categoryName:
                            categoryMap[p.dataPointCategoryId]?.code ?? '—',
                        subcategoryName:
                            subcategoryMap[p.dataPointSubcategoryId]?.code ??
                            '',
                    }));

                setAllDataPoints(enriched);
            } catch (error) {
                console.error('Error loading data points:', error);
            } finally {
                setIsLoading(false);
            }
        }

        loadData();
    }, [individualId]);

    const filteredAndSorted = useMemo(() => {
        let result = [...allDataPoints];

        if (searchTerm) {
            const term = searchTerm.toLowerCase().replace(/[$,]/g, '');
            result = result.filter(p => {
                const formattedDate = formatDate(p.date); // MM/DD/YYYY
                const rawDate = p.date ?? ''; // YYYY-MM-DD
                const rawAmount = p.amount != null ? String(p.amount) : '';
                const formattedAmount = formatAmount(p.amount).replace(
                    /[$,]/g,
                    '',
                );

                return (
                    p.categoryName.toLowerCase().includes(term) ||
                    p.subcategoryName.toLowerCase().includes(term) ||
                    formattedDate.includes(term) ||
                    rawDate.includes(term) ||
                    rawAmount.includes(term) ||
                    formattedAmount.includes(term)
                );
            });
        }

        Object.entries(selections).forEach(([column, selectedValues]) => {
            if (selectedValues?.length > 0) {
                result = result.filter(item =>
                    selectedValues.includes(item[column]?.toString()),
                );
            }
        });

        result.sort((a, b) => {
            const { sort, order } = sortConfig;
            let aVal = a[sort];
            let bVal = b[sort];

            let primaryCmp;
            if (sort === SortOptions.AMOUNT) {
                aVal = aVal ?? 0;
                bVal = bVal ?? 0;
                primaryCmp =
                    order === OrderOptions.ASCENDING
                        ? aVal - bVal
                        : bVal - aVal;
            } else {
                aVal = (aVal ?? '').toString().toLowerCase();
                bVal = (bVal ?? '').toString().toLowerCase();
                const cmp = aVal.localeCompare(bVal);
                primaryCmp = order === OrderOptions.ASCENDING ? cmp : -cmp;
            }

            if (primaryCmp !== 0) return primaryCmp;

            // Tiebreakers: category name asc, then subcategory name asc
            const catCmp = (a.categoryName ?? '')
                .toLowerCase()
                .localeCompare((b.categoryName ?? '').toLowerCase());
            if (catCmp !== 0) return catCmp;

            return (a.subcategoryName ?? '')
                .toLowerCase()
                .localeCompare((b.subcategoryName ?? '').toLowerCase());
        });

        return result;
    }, [allDataPoints, searchTerm, selections, sortConfig]);

    const totalPages = Math.max(
        1,
        Math.ceil(filteredAndSorted.length / ITEMS_PER_PAGE),
    );
    const paginatedItems = filteredAndSorted.slice(
        (currentPage - 1) * ITEMS_PER_PAGE,
        currentPage * ITEMS_PER_PAGE,
    );

    function handleSort(sort) {
        setSortConfig(prev => ({
            sort,
            order:
                prev.sort === sort && prev.order === OrderOptions.ASCENDING
                    ? OrderOptions.DESCENDING
                    : OrderOptions.ASCENDING,
        }));
        setCurrentPage(1);
    }

    function handleSearch(e) {
        setSearchTerm(e.target.value);
        setCurrentPage(1);
    }

    function handleApply(column, selectedOptions) {
        setSelections(prev => ({ ...prev, [column]: selectedOptions }));
        setCurrentPage(1);
    }

    function clearFilters() {
        setSelections({});
        setSearchTerm('');
        setCurrentPage(1);
    }

    function openFilterModal(column) {
        setActiveModal(column);
    }

    function closeFilterModal() {
        setActiveModal(null);
    }

    function getOptions(column) {
        return Array.from(
            new Set(allDataPoints.map(item => item[column]?.toString())),
        )
            .filter(Boolean)
            .sort((a, b) => a.localeCompare(b));
    }

    function renderSortArrow(sort) {
        if (sortConfig.sort !== sort) return null;
        return (
            <span className="mx-2">
                <svg width="11" height="6" viewBox="0 0 11 6" fill="none">
                    <path
                        d={
                            sortConfig.order === OrderOptions.ASCENDING
                                ? 'M1 5.25L5.25 0.75L9.5 5.25'
                                : 'M1 0.75L5.25 5.25L9.5 0.75'
                        }
                        stroke="#2F2B43"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            </span>
        );
    }

    const hasActiveFilters =
        searchTerm || Object.values(selections).some(v => v && v.length > 0);

    return (
        <div style={{ width: '100%' }}>
            {allDataPoints.length > 0 && (
                <div className="d-flex justify-content-between align-items-center py-3">
                    <div className="input-group" style={{ maxWidth: 320 }}>
                        <input
                            type="text"
                            className="form-control"
                            placeholder={getText('msg_69807f2cf6d5')}
                            value={searchTerm}
                            onChange={handleSearch}
                        />
                        <span className="input-group-text">
                            <i className="bi bi-search" />
                        </span>
                    </div>
                    {hasActiveFilters && (
                        <span
                            type="button"
                            onClick={clearFilters}
                            className="ing-text-buttons mx-3"
                        >
                            <i className="bi bi-arrow-counterclockwise me-2" />
                            {getText('msg_381c4bf1d409')}
                        </span>
                    )}
                </div>
            )}

            {isLoading ? (
                <div className="text-center mt-5">
                    <Spinner width={50} className={styles.loadingSpinner} />
                </div>
            ) : filteredAndSorted.length === 0 ? (
                <NotFoundBox
                    backgroundColor="#f0f4ff"
                    title={getText('msg_55cbceaf0dff')}
                    description={getText('msg_89003cfe30d4')}
                    width="50%"
                />
            ) : (
                <>
                    <div className="table-responsive-xxl w-100">
                        <table className="table table-hover align-middle">
                            <thead className="custom-table-header table-light">
                                <tr>
                                    <th
                                        style={{ cursor: 'pointer' }}
                                        className="position-relative"
                                        onClick={() =>
                                            handleSort(SortOptions.CATEGORY)
                                        }
                                    >
                                        <span className="table-column-header-text">
                                            {getText('msg_660afcf6eb49')}{' '}
                                            {renderSortArrow(
                                                SortOptions.CATEGORY,
                                            )}
                                        </span>
                                        <span
                                            onClick={e => {
                                                e.stopPropagation();
                                                openFilterModal('categoryName');
                                            }}
                                            className={classNames(
                                                styles.cursorPointer,
                                                'pe-3',
                                            )}
                                        >
                                            {renderFilterIcon(
                                                selections['categoryName']
                                                    ?.length > 0,
                                            )}
                                        </span>
                                        {activeModal === 'categoryName' && (
                                            <FilterModal
                                                column={activeModal}
                                                options={getOptions(
                                                    'categoryName',
                                                )}
                                                selectedOptions={
                                                    selections[
                                                        'categoryName'
                                                    ] || []
                                                }
                                                onApply={handleApply}
                                                onClose={closeFilterModal}
                                            />
                                        )}
                                    </th>
                                    <th
                                        style={{ cursor: 'pointer' }}
                                        className="position-relative"
                                        onClick={() =>
                                            handleSort(SortOptions.SUBCATEGORY)
                                        }
                                    >
                                        <span className="table-column-header-text">
                                            {getText('msg_2639f53ec4c2')}{' '}
                                            {renderSortArrow(
                                                SortOptions.SUBCATEGORY,
                                            )}
                                        </span>
                                        <span
                                            onClick={e => {
                                                e.stopPropagation();
                                                openFilterModal(
                                                    'subcategoryName',
                                                );
                                            }}
                                            className={classNames(
                                                styles.cursorPointer,
                                                'pe-3',
                                            )}
                                        >
                                            {renderFilterIcon(
                                                selections['subcategoryName']
                                                    ?.length > 0,
                                            )}
                                        </span>
                                        {activeModal === 'subcategoryName' && (
                                            <FilterModal
                                                column={activeModal}
                                                options={getOptions(
                                                    'subcategoryName',
                                                )}
                                                selectedOptions={
                                                    selections[
                                                        'subcategoryName'
                                                    ] || []
                                                }
                                                onApply={handleApply}
                                                onClose={closeFilterModal}
                                            />
                                        )}
                                    </th>
                                    <th
                                        style={{ cursor: 'pointer' }}
                                        className="position-relative"
                                        onClick={() =>
                                            handleSort(SortOptions.DATE)
                                        }
                                    >
                                        <span className="table-column-header-text">
                                            {getText('msg_eb9a4bc1c0c1')}{' '}
                                            {renderSortArrow(SortOptions.DATE)}
                                        </span>
                                        <span
                                            onClick={e => {
                                                e.stopPropagation();
                                                openFilterModal('date');
                                            }}
                                            className={classNames(
                                                styles.cursorPointer,
                                                'pe-3',
                                            )}
                                        >
                                            {renderFilterIcon(
                                                selections['date']?.length > 0,
                                            )}
                                        </span>
                                        {activeModal === 'date' && (
                                            <FilterModal
                                                column={activeModal}
                                                options={getOptions('date')}
                                                selectedOptions={
                                                    selections['date'] || []
                                                }
                                                onApply={handleApply}
                                                onClose={closeFilterModal}
                                            />
                                        )}
                                    </th>
                                    <th
                                        style={{
                                            cursor: 'pointer',
                                            textAlign: 'right',
                                        }}
                                        onClick={() =>
                                            handleSort(SortOptions.AMOUNT)
                                        }
                                    >
                                        <span className="table-column-header-text">
                                            {getText('msg_918f0f1a0c8f')}{' '}
                                            {renderSortArrow(
                                                SortOptions.AMOUNT,
                                            )}
                                        </span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedItems.map(point => (
                                    <tr key={point.id}>
                                        <td>{point.categoryName}</td>
                                        <td>{point.subcategoryName}</td>
                                        <td>{formatDate(point.date)}</td>
                                        <td style={{ textAlign: 'right' }}>
                                            {formatAmount(point.amount)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <PaginationComponent
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                    />
                </>
            )}
        </div>
    );
}

export default IndividualDataPointsTab;
